import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import routeConfig from 'routes/config';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import { checkPermissionForPermissionString, getConfig, getRoutes } from 'layouts/helper';
import views from 'views';
import Layout from 'layouts/layout';
import AuthLayout from 'layouts/auth';
import PrivateRoute from './private';
import DashboardLayout from 'layouts/dashboard';
import useViewport from 'utils/hooks/useViewport'; // Import the custom hook
import { ENV } from 'utils/config';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';

const AppRoutes = (): JSX.Element => {
  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const { width } = useViewport(); // Use the custom hook
  const isMobile = width <= 576; // Define the mobile breakpoint

  const { paymentConfig } = usePaymentConfig();
  const { msoPermissionsList } = paymentConfig;
  const view = activeOrg?.is_partner
    ? views.partner
    : views.dispensary;

  const entityPermissions = msoPermissionsList?.entityContexts?.find(
    (entity) => entity.id === activeOrg?.treez_entity_id
  );

  const virtualTerminalPermission = checkPermissionForPermissionString('tp::virtualTerminalAccess' ,entityPermissions)

  const renderRoutes = (
    layout: string,
    permissions?: PAYMENT_CONFIG.IEntityPermissionObject
  ) => {
    return <Routes>{getRoutes(getConfig(layout, view), permissions)}</Routes>;
  };

  return (
    <>
      <Routes>
        {/* authenticated routes */}
        <Route element={<PrivateRoute />}>
          {/** landing page */}
          <Route
            path="/"
            element={
              <Navigate
                to={`/${routeConfig.STORES.layout}/${routeConfig.STORES.LIST.path}`}
                
                replace
              />
            }
          />
          <Route element={<Layout />}>
            {/* store listing */}
            <Route
              path={`${routeConfig.STORES.layout}/*`}
              element={renderRoutes(routeConfig.STORES.layout)}
            />
          </Route>
          {/* dashboard routes  */}
          <Route path="/:orgId" element={<DashboardLayout />}>
            {activeOrg?.is_partner && (
              <>
                <Route
                  path={`${routeConfig.PARTNER_STORES.layout}/*`}
                  element={renderRoutes(routeConfig.PARTNER_STORES.layout)}
                />
                <Route
                  path={`${routeConfig.PARTNER_TRANSACTIONS.layout}/*`}
                  element={renderRoutes(
                    routeConfig.PARTNER_TRANSACTIONS.layout
                  )}
                />
                <Route
                  index
                  element={
                    <Navigate
                      to={`${routeConfig.PARTNER_STORES.layout}/${routeConfig.PARTNER_STORES.stores.path}`}
                      replace
                    />
                  }
                />
                <Route
                  path={`${routeConfig.MARKETPLACE.layout}/*`}
                  element={renderRoutes(routeConfig.MARKETPLACE.layout)}
                />
              </>
            )}
            {!activeOrg?.is_partner && (
              <>
                <Route
                  path={`${routeConfig.ANALYTICS.layout}/*`}
                  element={renderRoutes(routeConfig.ANALYTICS.layout)}
                />
                <Route
                  path={`${routeConfig.VIRTUAL_TERMINALS.layout}/*`}
                  element={renderRoutes(routeConfig.VIRTUAL_TERMINALS.layout)}
                />
                <Route
                  path={`${routeConfig.PAYMENTS.layout}/*`}
                  element={renderRoutes(routeConfig.PAYMENTS.layout)}
                />
                <Route
                  path={`${routeConfig.APPLICATION_CENTER.layout}/*`}
                  element={renderRoutes(routeConfig.APPLICATION_CENTER.layout)}
                />
                <Route
                  path={`${routeConfig.CUSTOMERS.layout}/*`}
                  element={renderRoutes(routeConfig.CUSTOMERS.layout)}
                />
                <Route
                  index
                  element={
                    <Navigate
                    to={
                      isMobile
                        ? `${routeConfig.VIRTUAL_TERMINALS.layout}/${routeConfig.VIRTUAL_TERMINALS.checkout.path}`
                        : `${routeConfig.ANALYTICS.layout}/${routeConfig.ANALYTICS.HOME.path}`
                    }
                    replace
                    />
                  }
                />
                <Route
                  path={`${routeConfig.SETTINGS.layout}/*`}
                  element={renderRoutes(
                    routeConfig.SETTINGS.layout,
                    entityPermissions
                  )}
                />
                <Route
                  path={`${routeConfig.MARKETPLACE.layout}/*`}
                  element={renderRoutes(routeConfig.MARKETPLACE.layout)}
                />
              </>
            )}
            <Route
              path={`${routeConfig.DEVELOPERS.layout}/*`}
              element={renderRoutes(routeConfig.DEVELOPERS.layout)}
            />
          </Route>
        </Route>

        {/* unauthenticated routes */}
        <Route element={<Layout />}>
          {/** /auth */}
          <Route
            path={`${routeConfig.AUTH.layout}/*`}
            element={<AuthLayout />}
          />
          {/** email verification */}
          <Route
            path={`${routeConfig.VERIFICATION.path}`}
            element={
              <Navigate
                to={`${routeConfig.AUTH.layout}${routeConfig.AUTH.LOGIN.path}`}
                replace
              />
            }
          />
        </Route>
        <Route path="*" element={<Navigate to={routeConfig.ROOT} replace />} />
      </Routes>
    </>
  );
};

export default AppRoutes;
